import React from 'react'
import { Slider_ } from 'monica-alexandria'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import {
    read_leave_request_date_label,
    read_leave_request_key,
    read_leave_request_owner_name
} from '../../utils/leaves/leave_approvals'

const NotificationsSlider = styled.div`
    display: grid;
    gap: var(--normalPads);
`

const NotificationCard = styled.div`
    display: grid;
    gap: var(--normalPads);
    padding: var(--normalPads);
    background: ${(p) => p.theme.background};
    box-shadow: ${(p) => p.theme.out};
    border-radius: var(--smallRadius);
    border-left: 0.4rem solid var(--warning);
`

const NotificationHeader = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: var(--normalPads);
`

const NotificationTitle = styled.div`
    display: grid;
    gap: var(--smallPads);
`

const NotificationStatus = styled.div`
    width: fit-content;
    padding: var(--smallPads);
    background: ${(p) => p.theme.low};
    border-radius: var(--smallRadius);

    h6 {
        color: var(--warning);
    }
`

const NotificationDetails = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: var(--smallPads);

    @media (max-width: 70rem) {
        grid-template-columns: 1fr;
    }
`

const NotificationDetail = styled.div`
    display: grid;
    gap: 0.4rem;
    padding: var(--smallPads);
    background: ${(p) => p.theme.low};
    border-radius: var(--smallRadius);
`

const EmptyNotifications = styled.div`
    display: grid;
    gap: var(--smallPads);
    padding: var(--largePads);
    background: ${(p) => p.theme.background};
    box-shadow: ${(p) => p.theme.in};
    border-radius: var(--smallRadius);
`

/* Read the reason text that should appear on one notification card. */
const read_leave_request_reason_label = (leaveRequest, t) => {
    const reason = `${leaveRequest?.reason || leaveRequest?.notes || ''}`.trim()
    const labels = {
        true: reason,
        false: t('No reason added')
    }

    return labels[String(Boolean(reason))]
}

/* Read the Slider_ action props for the current notification list. */
const read_slider_action = (notifications, onApplicationsClick, t) => {
    const hasNotifications = Boolean(notifications.length)
    const actions = {
        true: {
            text: t('Open applications'),
            onClick: onApplicationsClick
        },
        false: {
            text: '',
            onClick: null
        }
    }

    return actions[String(hasNotifications)]
}

/* Renders a single personal leave approval notification card. */
const NavbarNotificationCard = (props) => {
    return (
        <NotificationCard>
            <NotificationHeader>
                <NotificationTitle>
                    <h4>{read_leave_request_owner_name(props.notification)}</h4>
                    <p>{props.notification?.department}</p>
                </NotificationTitle>
                <NotificationStatus>
                    <h6>{props.t('Approval needed')}</h6>
                </NotificationStatus>
            </NotificationHeader>
            <NotificationDetails>
                <NotificationDetail>
                    <h6>{props.t('Leave')}</h6>
                    <p>{props.t('Personal leave')}</p>
                </NotificationDetail>
                <NotificationDetail>
                    <h6>{props.t('Dates')}</h6>
                    <p>{read_leave_request_date_label(props.notification?.startDate)} - {read_leave_request_date_label(props.notification?.endDate)}</p>
                </NotificationDetail>
                <NotificationDetail>
                    <h6>{props.t('Days')}</h6>
                    <p>{props.notification?.countedDays} {props.t('days')}</p>
                </NotificationDetail>
                <NotificationDetail>
                    <h6>{props.t('Reason')}</h6>
                    <p>{read_leave_request_reason_label(props.notification, props.t)}</p>
                </NotificationDetail>
            </NotificationDetails>
        </NotificationCard>
    )
}

/* Renders the list of live approval notifications. */
const NavbarNotificationsList = (props) => {
    return (
        <NotificationsSlider>
            {props.notifications.map((notification) => {
                return (
                    <NavbarNotificationCard
                        key={read_leave_request_key(notification)}
                        notification={notification}
                        t={props.t}
                    />
                )
            })}
        </NotificationsSlider>
    )
}

/* Renders the empty state when the current user has nothing to approve. */
const NavbarEmptyNotifications = (props) => {
    return (
        <EmptyNotifications>
            <h4>{props.t('No approvals pending')}</h4>
            <p>{props.t('Personal leave requests you can approve will appear here.')}</p>
        </EmptyNotifications>
    )
}

const notificationContentLookup = {
    true: NavbarNotificationsList,
    false: NavbarEmptyNotifications
}

/* Renders the navbar notification slider for personal leave approvals. */
export const Navbar_Notifications_Slider = (props) => {
    const { t } = useTranslation()
    const hasNotifications = Boolean(props.notifications.length)
    const NotificationContent = notificationContentLookup[String(hasNotifications)]
    const sliderAction = read_slider_action(props.notifications, props.onApplicationsClick, t)

    if (!props.open) {
        return null
    }

    return (
        <Slider_
            title={t('Notifications')}
            text={sliderAction.text}
            onClick={sliderAction.onClick}
            onClose={props.onClose}
        >
            <NotificationContent
                notifications={props.notifications}
                t={t}
            />
        </Slider_>
    )
}
